"use client"

import type { Message } from "ai"
import { Bot, User, FileText, ChevronDown, ChevronUp, ExternalLink } from "lucide-react"
import ReactMarkdown from "react-markdown"
import { useState, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "../ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"
import { cn } from "../lib/utils"
import { ResponseStream } from "../ui/response-stream"
import { MarkdownRenderer } from "../ui/markdown-renderer"
import AIChartRenderer from "./AIChartRenderer"
import GenUIRenderer from "./GenUIRenderer"
// import Image from "next/image"

// Helper type for transforming tool calls
interface ToolInvocation {
  toolName: string;
  toolCallId: string;
  state: "start" | "stream" | "partial-call" | "call" | "result" | "error";
  args?: any;
  result?: any;
}

export function ChatMessageBubble(props: {
  message: Message
  aiEmoji?: string
  sources: any[]
}) {
  const [showSources, setShowSources] = useState(false)
  const [streamingDone, setStreamingDone] = useState(() => {
    if (props.message.role === "user") return true
    const content = props.message.content || ""
    return content.length > 0
  })
  const [processedContent, setProcessedContent] = useState<string | null>(null)
  const [chartData, setChartData] = useState<any>(null)
  const [toolInvocations, setToolInvocations] = useState<ToolInvocation[]>([])
  const isUser = props.message.role === "user"
  const hasSources = props.sources && props.sources.length > 0
  
  // Create memoized components to prevent re-renders
  const memoizedGenUIRenderer = useMemo(() => {
    if (toolInvocations.length > 0) {
      return <GenUIRenderer toolInvocations={toolInvocations} />;
    }
    return null;
  }, [JSON.stringify(toolInvocations)]);
  
  const memoizedChartRenderer = useMemo(() => {
    if (chartData && toolInvocations.length === 0) {
      return <AIChartRenderer chartData={chartData} />;
    }
    return null;
  }, [JSON.stringify(chartData), toolInvocations.length]);
  
  // Handle table suggestion clicks
  useEffect(() => {
    const handleSuggestion = (event: any) => {
      if (event.detail?.query) {
        console.log("ChatMessageBubble: Table suggestion clicked:", event.detail.query);
        // You can implement the action here, such as sending the query to the AI
        // For example: sending a new message with the query
      }
    };
    
    // Add event listener
    document.addEventListener('ai-table-suggestion', handleSuggestion);
    
    // Clean up
    return () => {
      document.removeEventListener('ai-table-suggestion', handleSuggestion);
    };
  }, []);
  
  // Process tool calls when message changes or streaming completes
  useEffect(() => {
    if (props.message && !isUser) {
      const extractedTools = extractToolInvocations(props.message);
      if (extractedTools.length > 0) {
        // Make sure we're not adding duplicate chart tools
        const uniqueTools = extractedTools.filter((tool, index, self) => 
          self.findIndex(t => t.toolName === tool.toolName) === index
        );
        setToolInvocations(uniqueTools);
      }
    }
  }, [props.message, isUser]);
  
  // Process content after streaming is done to extract charts, tables, etc.
  useEffect(() => {
    if (streamingDone && !isUser && props.message.content) {
      const content = props.message.content;
      let updatedContent = content;
      
      console.log("BUBBLE EXTRACTION: Content length:", content.length);
      console.log("BUBBLE EXTRACTION: Contains table markers:", content.includes("```table"));
      console.log("BUBBLE EXTRACTION: Current tool invocations:", toolInvocations);
      
      // Check for chart code blocks and extract the JSON
      const chartBlockRegex = /```chart\s*([\s\S]*?)```/g;
      let match;
      let chartFound = false;
      
      // Process all chart blocks in the content
      while ((match = chartBlockRegex.exec(content)) !== null) {
        if (match && match[1]) {
          try {
            console.log("BUBBLE: Processing chart block from content");
            chartFound = true;
            const jsonData = JSON.parse(match[1]);
            
            // Set chart data for legacy rendering (if needed)
            setChartData(jsonData);
            
            // Check if we already have this in toolInvocations
            const existingChartTool = toolInvocations.find(t => 
              t.toolName === 'search_and_generate_chart' || 
              t.toolName.includes('chart'));
            
            if (!existingChartTool) {
              // Add a new tool invocation
              console.log("BUBBLE: Adding chart tool invocation from content block");
              setToolInvocations(prev => [
                ...prev,
                {
                  toolName: "search_and_generate_chart", 
                  toolCallId: "chart-" + Date.now(),
                  state: "result",
                  result: jsonData
                }
              ]);
            }
            
            // Remove the chart block from the content
            updatedContent = updatedContent.replace(match[0], '');
          } catch (error) {
            console.error("BUBBLE: Failed to parse chart JSON in content block", error);
          }
        }
      }
      
      // Check for table code blocks and extract the JSON
      const tableBlockRegex = /```table\s*([\s\S]*?)```/g;
      let tableMatch;
      let tableFound = false;
      
      console.log("BUBBLE EXTRACTION: Looking for table blocks with regex:", tableBlockRegex);
      
      // Process all table blocks in the content
      while ((tableMatch = tableBlockRegex.exec(content)) !== null) {
        if (tableMatch && tableMatch[1]) {
          try {
            console.log("BUBBLE EXTRACTION: Found table block match:", tableMatch[0].substring(0, 100) + "...");
            console.log("BUBBLE EXTRACTION: Table JSON data:", tableMatch[1].substring(0, 100) + "...");
            tableFound = true;
            const jsonData = JSON.parse(tableMatch[1]);
            
            console.log("BUBBLE EXTRACTION: Parsed table data successfully:", 
              jsonData.data ? `${jsonData.data.length} rows` : "No data",
              "config:", jsonData.config ? "present" : "missing"
            );
            
            // Check if we already have this in toolInvocations
            const existingTableTool = toolInvocations.find(t => 
              t.toolName === 'search_and_generate_table' || 
              t.toolName.includes('table'));
            
            if (!existingTableTool) {
              // Add a new tool invocation
              console.log("BUBBLE EXTRACTION: Adding table tool invocation from content block");
              setToolInvocations(prev => [
                ...prev,
                {
                  toolName: "search_and_generate_table", 
                  toolCallId: "table-" + Date.now(),
                  state: "result",
                  result: jsonData
                }
              ]);
            } else {
              console.log("BUBBLE EXTRACTION: Table tool already exists, not adding duplicate");
            }
            
            // Remove the table block from the content
            updatedContent = updatedContent.replace(tableMatch[0], '');
            console.log("BUBBLE EXTRACTION: Removed table block from content");
          } catch (error) {
            console.error("BUBBLE EXTRACTION: Failed to parse table JSON in content block", error);
          }
        }
      }
      
      // Try alternative table format if no table blocks were found
      if (!tableFound && !content.includes("```table")) {
        console.log("BUBBLE EXTRACTION: No ```table blocks found, looking for alternative formats");
        
        // Check for structured table data without code blocks
        const tableDataPattern = /\{\s*"data"\s*:\s*\[\s*\{\s*"Level".*\}\s*\]/;
        const structuredMatch = content.match(tableDataPattern);
        
        if (structuredMatch) {
          console.log("BUBBLE EXTRACTION: Found potential structured table data");
          try {
            const potentialTableData = structuredMatch[0];
            const jsonData = JSON.parse(potentialTableData);
            
            if (jsonData && Array.isArray(jsonData.data)) {
              console.log("BUBBLE EXTRACTION: Successfully parsed alternative table data");
              tableFound = true;
              
              // Create a proper table result object
              const tableResult = {
                data: jsonData.data,
                config: {
                  title: "Table Data",
                  columns: Object.keys(jsonData.data[0]).map(key => ({
                    accessorKey: key,
                    header: key
                  })),
                  showSearch: true,
                  showPagination: true
                }
              };
              
              // Add to tool invocations if not already there
              if (!toolInvocations.some(t => t.toolName.includes('table'))) {
                console.log("BUBBLE EXTRACTION: Adding table tool invocation from alternative data");
                setToolInvocations(prev => [
                  ...prev,
                  {
                    toolName: "search_and_generate_table", 
                    toolCallId: "table-alt-" + Date.now(),
                    state: "result",
                    result: tableResult
                  }
                ]);
              }
              
              // Remove the JSON from the content
              updatedContent = updatedContent.replace(structuredMatch[0], '');
            }
          } catch (error) {
            console.error("BUBBLE EXTRACTION: Failed to parse alternative table data", error);
          }
        }
      }
      
      // If no chart blocks were found, look for raw JSON
      if (!chartFound && !tableFound && updatedContent === content) {
        // Try to match raw JSON that might represent a chart
        const jsonRegex = /\{\s*"chartType"\s*:.*\s*"data"\s*:\s*\[.*\]/g;
        const jsonMatch = content.match(jsonRegex);
        
        if (jsonMatch) {
          try {
            console.log("BUBBLE: Found potential chart JSON in raw content");
            const parsedJson = JSON.parse(jsonMatch[0]);
            if (parsedJson && parsedJson.chartType && Array.isArray(parsedJson.data)) {
              // Set chart data
              setChartData(parsedJson);
              
              // Add to tool invocations if not already there
              if (!toolInvocations.some(t => t.toolName.includes('chart'))) {
                console.log("BUBBLE: Adding chart tool invocation from raw JSON");
                setToolInvocations(prev => [
                  ...prev,
                  {
                    toolName: "search_and_generate_chart", 
                    toolCallId: "chart-" + Date.now(),
                    state: "result",
                    result: parsedJson
                  }
                ]);
              }
              
              // Remove the JSON from the content
              updatedContent = updatedContent.replace(jsonMatch[0], '');
            }
          } catch (error) {
            console.error("BUBBLE: Failed to parse raw JSON as chart data", error);
          }
        }
        
        // Try to match raw JSON that might represent a table
        const tableJsonRegex = /\{\s*"data"\s*:\s*\[.*\]\s*,\s*"config"\s*:/g;
        const tableJsonMatch = content.match(tableJsonRegex);
        
        if (tableJsonMatch) {
          try {
            console.log("BUBBLE: Found potential table JSON in raw content");
            const parsedJson = JSON.parse(tableJsonMatch[0]);
            if (parsedJson && Array.isArray(parsedJson.data) && parsedJson.config) {
              // Add to tool invocations if not already there
              if (!toolInvocations.some(t => t.toolName.includes('table'))) {
                console.log("BUBBLE: Adding table tool invocation from raw JSON");
                setToolInvocations(prev => [
                  ...prev,
                  {
                    toolName: "search_and_generate_table", 
                    toolCallId: "table-" + Date.now(),
                    state: "result",
                    result: parsedJson
                  }
                ]);
              }
              
              // Remove the JSON from the content
              updatedContent = updatedContent.replace(tableJsonMatch[0], '');
            }
          } catch (error) {
            console.error("BUBBLE: Failed to parse raw JSON as table data", error);
          }
        }
      }
      
      // Set the updated content
      setProcessedContent(updatedContent);
    }
  }, [streamingDone, isUser, props.message.content]);

  return (
    <div className={cn("flex w-full items-start gap-2 sm:gap-4 py-4", isUser ? "justify-end" : "justify-start")}>
      {!isUser && (
        <div className="flex-shrink-0 rounded-full bg-primary/10 p-1.5 sm:p-2">
          {props.aiEmoji ? (
            <img src="/images/logos/logo.png" alt="logo zynextech" width={24} height={24} className="h-5 w-5 sm:h-6 sm:w-6 text-primary rounded-full dark:invert" />
          ) : (
            <img src="/images/logos/logo.png" alt="logo zynextech" width={24} height={24} className="h-5 w-5 sm:h-6 sm:w-6 text-primary rounded-full dark:invert" />
          )}
        </div>
      )}

      <div
        className={cn(
          "flex flex-col space-y-2 max-w-[80%] rounded-lg px-4 py-3",
          isUser ? "bg-primary text-primary-foreground" : "bg-muted/50 backdrop-blur-sm",
        )}
      >
        <div className="prose dark:prose-invert prose-sm break-words text-sm">
          {isUser ? (
            <ReactMarkdown
              components={{
                a: ({node, ...props}) => (
                  <a {...props} className="text-primary underline" target="_blank" rel="noopener noreferrer" />
                ),
                p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                ul: ({children}) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                li: ({children}) => <li className="mb-1">{children}</li>,
                code({className, children, ...props}) {
                  const match = /language-(\w+)/.exec(className || "")
                  const language = match ? match[1] : ""

                  // Check for multiline code block vs inline code
                  const isMultiLineCode = match && className && className.includes("language-");

                  if (!isMultiLineCode) {
                    return (
                      <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground/90" {...props}>
                        {children}
                      </code>
                    )
                  }

                  return (
                    <div className="relative group my-3">
                      {language && (
                        <div className="absolute top-0 right-0 bg-muted/80 text-xs px-2 py-0.5 rounded-bl font-mono text-muted-foreground">
                          {language}
                        </div>
                      )}
                      <pre className="bg-muted/80 backdrop-blur-sm p-3 rounded-md overflow-x-auto text-sm font-mono">
                        <code className="text-foreground/90" {...props}>
                          {String(children).replace(/\n$/, "")}
                        </code>
                      </pre>
                    </div>
                  )
                },
              }}
            >
              {props.message.content}
            </ReactMarkdown>
          ) : (
            <div className="w-full min-w-full">
              {!streamingDone ? (
                <ResponseStream
                  key={`stream-${props.message.id || 'default'}`}
                  textStream={props.message.content || ""}
                  mode="blurIn"
                  className="text-sm"
                  speed={40}
                  onComplete={() => setStreamingDone(true)}
                />
              ) : (
                <>
                  {/* Render any GenUI components from tool invocations */}
                  {toolInvocations.length > 0 && (
                    <div className="mb-4">
                      {memoizedGenUIRenderer}
                    </div>
                  )}
                  
                  {/* Legacy chart rendering (as fallback) - only if no tool invocations */}
                  {chartData && toolInvocations.length === 0 && (
                    <div className="mb-4">
                      {memoizedChartRenderer}
                    </div>
                  )}
                  
                  {/* Render the regular message content without the chart blocks */}
                  <MarkdownRenderer 
                    content={processedContent || props.message.content || ""} 
                    className="text-sm"
                    skipChartExtraction={true}
                  />
                </>
              )}
            </div>
          )}
        </div>

        {hasSources && (
          <div className="mt-2 pt-2 border-t border-muted">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowSources(!showSources)}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Sources ({props.sources.length})</span>
                    {showSources ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{showSources ? "Hide sources" : "Show sources"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <AnimatePresence>
              {showSources && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2 mt-2">
                    {props.sources.map((source, i) => (
                      <div key={i} className="bg-muted p-2 rounded-md text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">Source {i + 1}</span>
                          {source.metadata?.source && (
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" asChild>
                              <a
                                href={source.metadata.source}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1"
                              >
                                <span>View</span>
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </Button>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap">{source.pageContent}</p>
                        {source.metadata?.loc?.lines !== undefined && (
                          <p className="text-muted-foreground mt-1 text-[10px]">
                            Lines {source.metadata.loc.lines.from} to {source.metadata.loc.lines.to}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {isUser && (
        <div className="flex-shrink-0 rounded-full bg-primary p-1.5 sm:p-2">
          <User className="h-4 w-4 sm:h-6 sm:w-6 text-primary-foreground" />
        </div>
      )}
    </div>
  )
}

// Helper function to extract tool calls from a message
function extractToolInvocations(message: Message): ToolInvocation[] {
  const toolInvocations: ToolInvocation[] = [];
  
  // Return empty array if no content
  if (!message || !message.content) return toolInvocations;
  
  try {
    // Check for direct tool calls in message object (with type guard)
    const messageWithTools = message as any;
    if ('tool_calls' in messageWithTools && messageWithTools.tool_calls && Array.isArray(messageWithTools.tool_calls)) {
      console.log("BUBBLE: Found tool_calls in message object", messageWithTools.tool_calls);
      
      messageWithTools.tool_calls.forEach((call: any) => {
        if (call && call.type === 'function') {
          try {
            const args = JSON.parse(call.function.arguments);
            
            toolInvocations.push({
              toolName: call.function.name,
              toolCallId: call.id,
              state: "call",
              args: args
            });
            
            // If it's a chart tool call, we might have the result already in the content
            if (call.function.name === 'search_and_generate_chart') {
              // Try to extract chart data from the message content
              const chartBlockRegex = /```chart\s*([\s\S]*?)```/g;
              let match;
              
              while ((match = chartBlockRegex.exec(message.content as string)) !== null) {
                if (match && match[1]) {
                  try {
                    const chartData = JSON.parse(match[1]);
                    
                    // Add a result entry for this tool call
                    toolInvocations.push({
                      toolName: call.function.name,
                      toolCallId: call.id,
                      state: "result",
                      args: args,
                      result: chartData
                    });
                    
                    break;
                  } catch (error) {
                    console.error("BUBBLE: Failed to parse chart JSON in tool call", error);
                  }
                }
              }
            }
            
            // If it's a table tool call, try to extract table data
            if (call.function.name === 'search_and_generate_table') {
              // Try to extract table data from the message content
              const tableBlockRegex = /```table\s*([\s\S]*?)```/g;
              let match;
              
              while ((match = tableBlockRegex.exec(message.content as string)) !== null) {
                if (match && match[1]) {
                  try {
                    const tableData = JSON.parse(match[1]);
                    
                    // Add a result entry for this tool call
                    toolInvocations.push({
                      toolName: call.function.name,
                      toolCallId: call.id,
                      state: "result",
                      args: args,
                      result: tableData
                    });
                    
                    break;
                  } catch (error) {
                    console.error("BUBBLE: Failed to parse table JSON in tool call", error);
                  }
                }
              }
            }
          } catch (error) {
            console.error("BUBBLE: Error processing function arguments", error);
          }
        }
      });
    }
    
    // Handle chart code blocks in content
    if (typeof message.content === 'string') {
      // Extract chart data from the content
      const chartBlockRegex = /```chart\s*([\s\S]*?)```/g;
      let match;
      
      while ((match = chartBlockRegex.exec(message.content)) !== null) {
        if (match && match[1]) {
          try {
            const chartData = JSON.parse(match[1]);
            
            // Check if we already have this chart in the tool invocations
            const chartExists = toolInvocations.some(tool => 
              tool.toolName === 'search_and_generate_chart' && 
              tool.state === 'result'
            );
            
            if (!chartExists) {
              toolInvocations.push({
                toolName: 'search_and_generate_chart',
                toolCallId: `chart-${Date.now()}`,
                state: 'result',
                result: chartData
              });
            }
          } catch (error) {
            console.error("BUBBLE: Failed to parse chart JSON in content block", error);
          }
        }
      }
      
      // Extract table data from the content
      const tableBlockRegex = /```table\s*([\s\S]*?)```/g;
      let tableMatch;
      
      while ((tableMatch = tableBlockRegex.exec(message.content)) !== null) {
        if (tableMatch && tableMatch[1]) {
          try {
            const tableData = JSON.parse(tableMatch[1]);
            
            // Check if we already have this table in the tool invocations
            const tableExists = toolInvocations.some(tool => 
              tool.toolName === 'search_and_generate_table' && 
              tool.state === 'result'
            );
            
            if (!tableExists) {
              toolInvocations.push({
                toolName: 'search_and_generate_table',
                toolCallId: `table-${Date.now()}`,
                state: 'result',
                result: tableData
              });
            }
          } catch (error) {
            console.error("BUBBLE: Failed to parse table JSON in content block", error);
          }
        }
      }
      
      // Extract image data from the content
      const imageBlockRegex = /```image\s*([\s\S]*?)```/g;
      let imageMatch;
      
      while ((imageMatch = imageBlockRegex.exec(message.content)) !== null) {
        if (imageMatch && imageMatch[1]) {
          try {
            const imageData = JSON.parse(imageMatch[1]);
            
            // Check if we already have this image in the tool invocations
            const imageExists = toolInvocations.some(tool => 
              tool.toolName === 'generate_image_from_data' && 
              tool.state === 'result'
            );
            
            if (!imageExists) {
              toolInvocations.push({
                toolName: 'generate_image_from_data',
                toolCallId: `image-${Date.now()}`,
                state: 'result',
                result: imageData
              });
            }
          } catch (error) {
            console.error("BUBBLE: Failed to parse image JSON in content block", error);
          }
        }
      }
      
      // Try to find raw JSON in the content that might represent chart data
      if (!toolInvocations.some(t => t.toolName === 'search_and_generate_chart')) {
        const chartJsonMatch = message.content.match(/\{\s*"chartType"\s*:.*\s*"data"\s*:\s*\[.*\]/);
        
        if (chartJsonMatch) {
          try {
            const chartData = JSON.parse(chartJsonMatch[0]);
            
            if (chartData && chartData.chartType && Array.isArray(chartData.data)) {
              toolInvocations.push({
                toolName: 'search_and_generate_chart',
                toolCallId: `chart-raw-${Date.now()}`,
                state: 'result',
                result: chartData
              });
            }
          } catch (error) {
            console.error("BUBBLE: Failed to parse raw chart JSON", error);
          }
        }
      }
      
      // Try to find raw JSON in the content that might represent table data
      if (!toolInvocations.some(t => t.toolName === 'search_and_generate_table')) {
        const tableJsonMatch = message.content.match(/\{\s*"data"\s*:\s*\[.*\]\s*,\s*"config"\s*:/);
        
        if (tableJsonMatch) {
          try {
            const tableData = JSON.parse(tableJsonMatch[0]);
            
            if (tableData && Array.isArray(tableData.data) && tableData.config) {
              toolInvocations.push({
                toolName: 'search_and_generate_table',
                toolCallId: `table-raw-${Date.now()}`,
                state: 'result',
                result: tableData
              });
            }
          } catch (error) {
            console.error("BUBBLE: Failed to parse raw table JSON", error);
          }
        }
      }
    }
    
    return toolInvocations;
  } catch (error) {
    console.error("BUBBLE: Error extracting tool invocations", error);
    return [];
  }
}
